// M004-F0749 (WebUI #191). The Open drawer of the Invoices tab: the invoice header and its lines, each line with its TXN code. A TXN opens the
// ONE shared transaction history drawer (owner 2026-10-10); this drawer closes first so the two never stack. Role dialog, focus moves in,
// Tab stays inside, Escape closes, focus returns to the opener.
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { invoiceApi } from '../../services/invoiceApi';
import TxChip from '../TxChip';
import { TxHistoryContext } from '../txHistoryContext';
import { deliveryText, errorInfo, formatDay, money, statusText } from './invoiceModel';
import '../bookings-admin/BookingDrawer.css';
import './InvoicesTab.css';

const FOCUSABLE = 'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

export default function InvoiceDrawer({ orgId, invoiceId, summary, ledgerDown, onClose, onDownload }) {
  const [state, setState] = useState({ status: 'loading', invoice: null, error: null });
  const outer = useContext(TxHistoryContext);
  const panel = useRef(null);
  const opener = useRef(typeof document !== 'undefined' ? document.activeElement : null);

  useEffect(() => {
    let current = true;
    setState({ status: 'loading', invoice: null, error: null });
    invoiceApi.get(orgId, invoiceId)
      .then((response) => {
        const invoice = response?.data?.data?.invoice;
        if (!current) return;
        if (!invoice) setState({ status: 'error', invoice: null, error: { code: '', message: 'The Ledger sent no invoice.' } });
        else setState({ status: 'ready', invoice, error: null });
      })
      .catch((cause) => { if (current) setState({ status: 'error', invoice: null, error: errorInfo(cause) }); });
    return () => { current = false; };
  }, [orgId, invoiceId]);

  useEffect(() => {
    const target = opener.current;
    panel.current?.querySelector('[data-testid="invoice-drawer-close"]')?.focus();
    return () => { if (target && typeof target.focus === 'function' && document.contains(target)) target.focus(); };
  }, []);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') { onClose?.(); return; }
      if (event.key !== 'Tab' || !panel.current) return;
      const items = [...panel.current.querySelectorAll(FOCUSABLE)];
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // A TXN chip inside this drawer opens the shared history drawer, after this drawer has closed.
  const history = useMemo(() => (outer && typeof outer.openTransaction === 'function'
    ? { ...outer, openTransaction: (args) => { onClose?.(); return outer.openTransaction(args); } }
    : outer), [outer, onClose]);
  const close = useCallback(() => onClose?.(), [onClose]);

  const invoice = state.invoice || summary;
  return (
    <div className="bkh-drawer-backdrop inv-drawer-backdrop" role="presentation" onClick={close}>
      <aside ref={panel} className="bkh-drawer inv-drawer" role="dialog" aria-modal="true" aria-labelledby="inv-drawer-title" data-testid="invoice-drawer" onClick={(event) => event.stopPropagation()}>
        <div className="bkh-drawer-header">
          <h3 id="inv-drawer-title">{invoice ? `Invoice ${invoice.number}` : 'Invoice'}</h3>
          <button type="button" data-testid="invoice-drawer-close" onClick={close}>Close</button>
        </div>
        {state.status === 'loading' ? <p role="status" className="inv-state">Opening the invoice{'\u2026'}</p> : null}
        {state.status === 'error' ? <p className="inv-error" role="alert">{state.error.code ? `${state.error.code}: ` : ''}{state.error.message}</p> : null}
        {invoice ? (
          <dl className="inv-facts">
            <div><dt>Customer</dt><dd>{invoice.customer_name}</dd></div>
            <div><dt>Issued</dt><dd>{formatDay(invoice.issue_date)}</dd></div>
            <div><dt>Due</dt><dd>{formatDay(invoice.due_date)}</dd></div>
            <div><dt>Total</dt><dd>{money(invoice.total_minor, invoice.currency)}</dd></div>
            <div><dt>Status</dt><dd>{statusText(invoice.status)}</dd></div>
            <div><dt>Delivery</dt><dd>{deliveryText(invoice.delivery)}</dd></div>
          </dl>
        ) : null}
        {state.status === 'ready' ? (
          <TxHistoryContext.Provider value={history}>
            <table className="inv-lines" data-testid="invoice-lines">
              <caption className="inv-sr">Invoice lines</caption>
              <thead><tr><th scope="col">Description</th><th scope="col" className="inv-num">Amount</th><th scope="col">Transaction</th></tr></thead>
              <tbody>
                {state.invoice.lines.map((line, index) => (
                  <tr key={`${line.txn_id}-${index}`}>
                    <td data-label="Description">{line.description}</td>
                    <td data-label="Amount" className="inv-num">{money(line.amount_minor, state.invoice.currency)}</td>
                    <td data-label="Transaction"><TxChip txnId={line.txn_id} txId={line.txn_id} orgId={String(orgId)} customerUserId={state.invoice.user_id} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TxHistoryContext.Provider>
        ) : null}
        {invoice ? (
          <div className="bkh-drawer-actions">
            <button type="button" disabled={ledgerDown} onClick={() => onDownload?.(invoice)}>Download PDF</button>
          </div>
        ) : null}
      </aside>
    </div>
  );
}
